import React, { useState, useEffect, useCallback } from 'react';
import { api, fmt } from '../utils/api';

function formatDate(d) {
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function StuckPendingReview({ onDismiss, onResolved }) {
  const [transactions, setTransactions] = useState(null);
  const [error, setError] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getStuckPendingTransactions();
      setTransactions(data.transactions);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markPosted = async (id) => {
    setResolvingId(id);
    try {
      await api.markTransactionPosted(id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      onResolved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setResolvingId(null);
    }
  };

  const dismiss = async (id) => {
    setResolvingId(id);
    try {
      await api.dismissPendingTransaction(id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      onResolved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div style={styles.panel}>
      <p style={styles.flaggedBanner}>
        ⚠️ DO THIS: these transactions have been stuck "pending" for over a week. For each one, we checked the rest
        of your history for a matching charge - if we found one, that means it almost certainly already posted under
        that other record, so this stuck copy should be removed. If we didn't find one, it likely just never got a
        confirming update from Plaid, so it's safe to mark posted (worth a quick check against your bank statement
        first). Your recommended action is highlighted below.
      </p>

      {error && <p style={styles.errorText}>{error}</p>}

      {transactions === null ? (
        <p style={styles.empty}>Loading...</p>
      ) : transactions.length === 0 ? (
        <p style={styles.empty}>Nothing stuck right now - you're all caught up here.</p>
      ) : (
        <div style={styles.list}>
          {transactions.map((t) => {
            const suggestRemove = t.suggestion === 'remove';
            return (
              <div key={t.id} style={styles.row}>
                <div style={styles.rowTop}>
                  <div style={styles.rowInfo}>
                    <div style={styles.rowDesc}>{t.description}</div>
                    <div style={styles.rowMeta}>
                      {formatDate(t.date)} &middot; {fmt.currency(t.amount)}
                    </div>
                  </div>
                  <div style={styles.rowActions}>
                    <button
                      style={suggestRemove ? styles.actionBtnNeutral : styles.actionBtnPrimary}
                      onClick={() => markPosted(t.id)}
                      disabled={resolvingId === t.id}
                      title="I checked my statement - this actually cleared"
                    >
                      {resolvingId === t.id ? '...' : '✓ Mark Posted'}
                    </button>
                    <button
                      style={suggestRemove ? styles.actionBtnPrimary : styles.actionBtnNeutral}
                      onClick={() => dismiss(t.id)}
                      disabled={resolvingId === t.id}
                      title="This was a hold that expired, or already posted elsewhere"
                    >
                      {resolvingId === t.id ? '...' : '🗑 Remove'}
                    </button>
                  </div>
                </div>

                {suggestRemove ? (
                  <div style={styles.suggestionRemove}>
                    💡 We think this already posted as <strong>"{t.match.description}"</strong> for {fmt.currency(t.match.amount)} on {formatDate(t.match.date)} - suggest <strong>Remove</strong>.
                  </div>
                ) : (
                  <div style={styles.suggestionMark}>
                    💡 Nothing else in your history matches this charge - suggest <strong>Mark Posted</strong> once you've confirmed it on your statement.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button style={styles.dismissBtn} onClick={onDismiss}>
        ← View all transactions instead
      </button>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  flaggedBanner: {
    color: '#ef4444',
    fontSize: '0.82rem',
    fontWeight: '600',
    lineHeight: '1.55',
    margin: '0 0 1.1rem 0',
    fontFamily: "'Syne', sans-serif",
  },
  errorText: { color: '#ef4444', fontSize: '0.85rem' },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  row: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.75rem 0.9rem',
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  rowInfo: { minWidth: 0 },
  rowDesc: { color: '#e5e7eb', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Syne', sans-serif" },
  rowMeta: { color: '#6b7280', fontSize: '0.75rem', marginTop: '2px', fontFamily: "'DM Mono', monospace" },
  rowActions: { display: 'flex', gap: '0.5rem', flexShrink: 0 },
  actionBtnPrimary: {
    background: 'rgba(0,212,170,0.15)',
    border: '1px solid #00d4aa',
    color: '#00d4aa',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: "'Syne', sans-serif",
  },
  actionBtnNeutral: {
    background: 'transparent',
    border: '1px solid #374151',
    color: '#6b7280',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: "'Syne', sans-serif",
  },
  suggestionRemove: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    marginTop: '0.6rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid #1e2a3a',
  },
  suggestionMark: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    marginTop: '0.6rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid #1e2a3a',
  },
  dismissBtn: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.78rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    textAlign: 'center',
    marginTop: '1.1rem',
  },
};
